import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { type AuthUser, fetchCurrentUser, requestMagicLink, logout as doLogout, setSessionToken, clearSession } from '../lib/auth';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
  authError: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const sessionToken = params.get('session_token');
    if (sessionToken) {
      setSessionToken(sessionToken);
      window.history.replaceState({}, '', window.location.pathname);
    }

    const error = params.get('auth_error');
    if (error) {
      setAuthError(error === 'expired' ? 'Your sign-in link has expired. Please request a new one.' : 'Authentication error. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }

    fetchCurrentUser().then(u => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  // Periodic session validation (every 5 min) + on visibility change
  useEffect(() => {
    if (!user) return;

    const validate = async () => {
      const u = await fetchCurrentUser();
      if (!u) {
        setUser(null);
        clearSession();
      }
    };

    const interval = setInterval(validate, 5 * 60 * 1000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') validate();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  const login = useCallback(async (email: string) => {
    setAuthError(null);
    return requestMagicLink(email);
  }, []);

  const logout = useCallback(async () => {
    await doLogout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, authError }}>
      {children}
    </AuthContext.Provider>
  );
}
